import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { initPushNotifications, unregisterPushNotifications } from '../utils/pushNotifications';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const saved = localStorage.getItem('user');
    if (token && saved) {
      setUser(JSON.parse(saved));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Resume push registration for already-logged-in sessions on native.
      initPushNotifications();
    }
    setLoading(false);
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    initPushNotifications();
  };

  const logout = () => {
    unregisterPushNotifications();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const isAuthenticated = !!user;

  const hasRole = (...roles) => user && roles.includes(user.role);

  // v1.9.22 — permissions now come from the server (role_permissions table).
  // Fallback map is kept only for old session tokens issued before the
  // upgrade so users don't get locked out until they next log in.
  const FALLBACK_MAP = {
    admin:       ['members','attendance','spiritual','finance','stock','reports','settings','info','finance.cr.approve','finance.pv.approve_admin'],
    pastor:      ['members','attendance','spiritual','finance','reports','info'],
    finance:     ['finance','stock','reports','info','finance.pv.approve_finance'],
    cashier:     ['finance','reports','info','finance.cr.approve'],
    secretary:   ['members','attendance','spiritual','stock','reports','info'],
    cell_leader: ['members','attendance','spiritual','info'],
  };

  const grantedKeys = () => {
    if (!user) return [];
    if (Array.isArray(user.permissions)) return user.permissions;
    return FALLBACK_MAP[user.role] || [];
  };

  const canAccess = (section) => grantedKeys().includes(section);
  const canDo = (action) => grantedKeys().includes(action);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAuthenticated, hasRole, canAccess, canDo }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
